import { Matrix, Vector2, Vector3 } from '@babylonjs/core/Maths/math';
import { VertexBuffer } from '@babylonjs/core/Meshes/buffer';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { PolygonMeshBuilder } from '@babylonjs/core/Meshes/polygonMesh';

import { ExtrudeFeature } from '@kb3d/models/classes/features/extrude-feature';
import { IKbVector } from '@kb3d/models/classes/kb3d-models';
import { WorkerProcessorHost } from '@kb3d/models/interfaces/worker-types';
import { earcut } from '@kb3d/view/helpers/earcut';
import { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';
import { rotationToVector } from '@kb3d/view/helpers/math-helper';
import { parseSketchGeometry } from '@kb3d/view/modules/sketching/util';
import { FeatureGeometryProcessor, ProcessorData } from './base-processor';

const xztoxy = Matrix.FromArray([1, 0, 0, 0, 0, 0, -1, 0, 0, 1, 0, 0, 0, 0, 0, 1]);

// This is passed to the worker as additional information needed
export type ExtrudeGeometryWorkerPayload = {
    sketchNormal: IKbVector;
};

// This is processed and deserialized in the worker based on the worker payload
export type ExtrudeGeometryDependencyData = {
    sketchNormal: Vector3;
};
export class ExtrudeGeometryProcessor<T extends ExtrudeFeature = ExtrudeFeature> extends FeatureGeometryProcessor<
    ExtrudeFeature,
    ExtrudeGeometryDependencyData
> {
    readWorkerPayload(_: WorkerProcessorHost, data: ExtrudeGeometryWorkerPayload) {
        return {
            sketchNormal: new Vector3(data.sketchNormal.x ?? 0, data.sketchNormal.y ?? 0, data.sketchNormal.z ?? 0),
        };
    }

    updateMeshGeometry(
        feature: T,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>,
        dependencyData: ProcessorData<ExtrudeGeometryDependencyData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges, dependencyData);

        if (!dependencyData.data) {
            return newVertexData;
        }

        const sketchNormal = dependencyData.data.sketchNormal;

        if (
            newVertexData.positions &&
            newVertexData.positions.length &&
            // paths are used by parseSketchGeometry
            newVertexData.paths &&
            newVertexData.paths.length > 0 &&
            feature.size !== 0
        ) {
            const { segments, holes } = parseSketchGeometry(newVertexData);
            const extrudedMeshes: Mesh[] = [];

            for (let i = 0; i < segments.length; i++) {
                const shape = segments[i];
                const shapeHoles = holes.get(i);

                const rotToZ = rotationToVector(sketchNormal, new Vector3(0, 0, 1));
                const pathTransformToZ = new Matrix();
                const pathTransformToZInv = new Matrix();
                rotToZ.toRotationMatrix(pathTransformToZ);
                pathTransformToZ.invertToRef(pathTransformToZInv);

                for (let i = 0; i < shape.length; i++) {
                    Vector3.TransformCoordinatesToRef(shape[i], pathTransformToZ, shape[i]);
                }

                const shape2d = shape.map(point => new Vector2(point.x, point.y));

                const builder = new PolygonMeshBuilder(feature._dynamicId, shape2d, this.scene, earcut);
                if (shapeHoles) {
                    shapeHoles.forEach(holePoints => {
                        for (let i = 0; i < holePoints.length; i++) {
                            Vector3.TransformCoordinatesToRef(holePoints[i], pathTransformToZ, holePoints[i]);
                        }
                        const hole2d = holePoints.map(point => new Vector2(point.x, point.y));
                        builder.addHole(hole2d);
                    });
                }
                const extruded = builder.build(true, Math.abs(feature.size), 0.9);
                xztoxy.multiplyToRef(pathTransformToZInv, pathTransformToZInv);

                extruded.bakeTransformIntoVertices(pathTransformToZInv);
                extrudedMeshes.push(extruded);
            }

            const extrudedAll = Mesh.MergeMeshes(extrudedMeshes, true);
            if (extrudedAll) {
                applyExtrusion(newVertexData, extrudedAll, false);
                extrudedAll.dispose();
            }
        }

        return newVertexData;
    }
}

export function applyExtrusion(newVertexData: Partial<SubmeshVertexData>, extruded: Mesh, reverse = false) {
    if (extruded.geometry) {
        // Store the indices used for the extrude so that they can be merged into the new geometry
        const newIndices = extruded.geometry.getIndices(false, true);
        const newNormals = extruded.geometry.getVerticesData(VertexBuffer.NormalKind, false, true);
        const newUvs = extruded.geometry.getVerticesData(VertexBuffer.UVKind, false, true);
        const newPositions = extruded.geometry.getVerticesData(VertexBuffer.PositionKind, false, true);
        if (newPositions && reverse) {
            for (let i = 2; i < newPositions.length; i += 3) {
                newPositions[i] = -newPositions[i];
            }
        }

        normalizeGeometry(newVertexData);

        if (!newVertexData.indices) {
            newVertexData.indices = newIndices;
        } else if (newIndices) {
            const concatArr = new Int32Array(newVertexData.indices.length + newIndices.length);
            concatArr.set(newVertexData.indices);
            if (newVertexData.positions) {
                const pushAmount = newVertexData.positions.length / 3;
                for (let i = 0; i < newIndices.length; i++) {
                    newIndices[i] += pushAmount;
                }
            }
            concatArr.set(newIndices, newVertexData.indices.length);
            newVertexData.indices = concatArr;
        }

        if (!newVertexData.positions) {
            newVertexData.positions = newPositions;
        } else if (newPositions) {
            const concatArr = new Float32Array(newVertexData.positions.length + newPositions.length);
            concatArr.set(newVertexData.positions);
            concatArr.set(newPositions, newVertexData.positions.length);
            newVertexData.positions = concatArr;
        }

        if (!newVertexData.normals) {
            newVertexData.normals = newNormals;
        } else if (newNormals) {
            const concatArr = new Float32Array(newVertexData.normals.length + newNormals.length);
            concatArr.set(newVertexData.normals);
            concatArr.set(newNormals, newVertexData.normals.length);
            newVertexData.normals = concatArr;
        }

        if (!newVertexData.uvs) {
            newVertexData.uvs = newUvs;
        } else if (newUvs) {
            const concatArr = new Float32Array(newVertexData.uvs.length + newUvs.length);
            concatArr.set(newVertexData.uvs);
            concatArr.set(newUvs, newVertexData.uvs.length);
            newVertexData.uvs = concatArr;
        }

        const baseMaterialIndex = newVertexData.submeshData ? newVertexData.submeshData[0].materialIndex : 0;
        const totalIndicesResultCount = newVertexData.indices!.length;
        const totalVerticesResultCount = newVertexData.positions!.length / 3;

        newVertexData.submeshData = [];
        newVertexData.submeshData.push({
            materialIndex: baseMaterialIndex,
            verticesStart: 0,
            verticesCount: totalVerticesResultCount,
            indexStart: 0,
            indexCount: totalIndicesResultCount,
        });
    }
}

export function normalizeGeometry(newVertexData: Partial<SubmeshVertexData>) {
    if (!newVertexData.indices) {
        // If there are no indices, throw away existing vertices because they won't be used
        newVertexData.positions = null;
        newVertexData.normals = null;
        newVertexData.uvs = null;
    } else {
        const indices = newVertexData.indices;
        let maxIdx = 0;
        for (let i = 0; i < indices.length; i++) {
            if (maxIdx < indices[i]) {
                maxIdx = indices[i];
            }
        }
        const usedVertexLength = maxIdx + 1;

        const positions = new Float32Array(usedVertexLength * 3);
        const normals = new Float32Array(usedVertexLength * 3);
        const uvs = new Float32Array(usedVertexLength * 2);

        if (newVertexData.positions) {
            if (Array.isArray(newVertexData.positions)) {
                positions.set(newVertexData.positions.slice(0, positions.length));
            } else {
                positions.set(newVertexData.positions.subarray(0, positions.length));
            }
        }
        if (newVertexData.normals) {
            if (Array.isArray(newVertexData.normals)) {
                normals.set(newVertexData.normals.slice(0, normals.length));
            } else {
                normals.set(newVertexData.normals.subarray(0, normals.length));
            }
        }
        if (newVertexData.uvs) {
            if (Array.isArray(newVertexData.uvs)) {
                uvs.set(newVertexData.uvs.slice(0, uvs.length));
            } else {
                uvs.set(newVertexData.uvs.subarray(0, uvs.length));
            }
        }
        newVertexData.positions = positions;
        newVertexData.normals = normals;
        newVertexData.uvs = uvs;
    }
}
